/**
 * Enhanced Audit Logs - Main Component (Refactored)
 * Split into: AuditLogFilters, AuditLogTable, useAuditLogs
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ScrollText, RefreshCw, ArrowUp, X, Bell } from 'lucide-react';

// Import sub-components
import { useAuditLogs } from './audit/useAuditLogs';
import { AuditLogFilters } from './audit/AuditLogFilters';
import { AuditLogTable } from './audit/AuditLogTable';

const EnhancedAuditLogs = () => {
  const {
    logs,
    loading,
    page,
    setPage,
    total,
    estimated,
    pageSize,
    loadingExactCount,
    showNewLogsBanner,
    newLogsCount,
    newLogsEstimated,
    filters,
    setFilters,
    fetchLogs,
    handleJumpToNewest,
    handleDismissBanner,
    handleGetExactCount,
    handleExport
  } = useAuditLogs();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-blue-400" />
            Audit Logs
          </h2>
          <p className="text-gray-400 mt-1">Track all system events and user actions</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => fetchLogs(page)}
            disabled={loading}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          {page > 1 && (
            <Button
              onClick={handleJumpToNewest}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <ArrowUp className="w-4 h-4 mr-2" />
              Jump to Newest
            </Button>
          )}
        </div>
      </div>

      {/* New Logs Banner */}
      {showNewLogsBanner && (
        <Card className="bg-blue-900/30 border-blue-500/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-blue-400 animate-pulse" />
                <span className="text-blue-300">
                  {newLogsEstimated ? '~' : ''}{newLogsCount.toLocaleString()} new log{newLogsCount !== 1 ? 's' : ''} available
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleJumpToNewest}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  View Newest
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismissBanner}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <AuditLogFilters 
        filters={filters} 
        setFilters={setFilters}
        onExport={handleExport}
        loading={loading}
      />

      {/* Log Table */}
      <AuditLogTable
        logs={logs}
        loading={loading}
        page={page}
        setPage={setPage}
        total={total}
        estimated={estimated}
        pageSize={pageSize}
        onGetExactCount={handleGetExactCount}
        loadingExactCount={loadingExactCount}
      />
    </div>
  );
};

export default EnhancedAuditLogs;
